/** Memory-only Connect records; importing retirement does not load the SDK. */
import type { StripeConnectInstance } from "@stripe/connect-js";
import type { FinanceContextResponse, FinanceSessionPurpose } from "@bw/shared/types/generated";
import type { FinanceConnectEmbedProps, FinanceConnectOperationalScope, FinanceConnectTrustedActorScope } from "./FinanceConnectEmbed";

export type Entry = { key: string; scope: FinanceConnectTrustedActorScope; purpose: FinanceSessionPurpose;
  context: FinanceContextResponse; fetchSession: FinanceConnectEmbedProps["fetchSession"];
  retired: boolean; operational: FinanceConnectOperationalScope | null; instance: StripeConnectInstance;
  onFailure: (() => void) | null };
export const instances = new Map<string, Entry>();
export const keyFor = (scope: FinanceConnectTrustedActorScope, purpose: FinanceSessionPurpose) =>
  JSON.stringify([scope.actorUserId, scope.orgId, scope.workSessionRevision, scope.bindingGeneration, purpose]);
export function allowed(scope: FinanceConnectTrustedActorScope, context: FinanceContextResponse, purpose: FinanceSessionPurpose) {
  return context.org_id === scope.orgId && context.binding_generation === scope.bindingGeneration &&
    context.work_session_revision === scope.workSessionRevision && context.account_bound &&
    context.allowed_actions.includes("org:finance_read") && context.allowed_session_purposes.includes(purpose) &&
    context.stripe_configuration.state === "configured";
}
export function retire(entry: Entry): void {
  if (entry.retired) return;
  entry.retired = true;
  if (instances.get(entry.key) === entry) instances.delete(entry.key);
  if (entry.instance) void entry.instance.logout().catch(() => {});
}
export function retireFinanceConnectScope(scope: FinanceConnectTrustedActorScope, purpose: FinanceSessionPurpose): void {
  const entry = instances.get(keyFor(scope, purpose));
  if (entry) retire(entry);
}
export function retireFinanceConnectActor(actorUserId: string): void {
  for (const entry of instances.values()) if (entry.scope.actorUserId === actorUserId) retire(entry);
}
export function reconcileFinanceConnectContext(actorUserId: string, orgId: string, context: FinanceContextResponse | null): void {
  for (const entry of instances.values()) {
    if (entry.scope.actorUserId !== actorUserId || entry.scope.orgId !== orgId) continue;
    if (!context || !allowed(entry.scope, context, entry.purpose) ||
        entry.context.authority_generation !== context.authority_generation ||
        JSON.stringify(entry.context.stripe_configuration) !== JSON.stringify(context.stripe_configuration)) retire(entry);
  }
}
